<template>
    <v-card class="scrollDiv" rounded="lg" :elevation="5">
        <div v-if="searchResults.length === 0 && loading" class="text-center pa-4">
            <div class="text-body-medium text-medium-emphasis mb-1">Searching: {{ searchQuery }}</div>
            <v-progress-linear color="info" indeterminate rounded />
        </div>

        <div v-else-if="searchResults.length === 0" class="text-center pa-4">
            <v-icon color="error" class="mb-1">$errorOutline</v-icon>
            <div class="text-body-medium text-medium-emphasis">No results found for: {{ searchQuery }}</div>
        </div>

        <div v-else>
            <v-divider class="search-divider" />

            <v-list lines="two" class="py-0">
                <template v-for="group in groupedResults" :key="group.type">
                    <!-- Section header -->
                    <div class="d-flex align-center px-4 pt-3 pb-1">
                        <span class="section-dot" :style="{ backgroundColor: group.color }"></span>
                        <span class="section-label">{{ group.label }}</span>
                    </div>

                    <!-- Result rows -->
                    <v-list-item v-for="(result, idx) in group.results" :key="result.id"
                        :to="goToView(result.type, result.id)"
                        :active="currentItem === getFlatIndex(group.type, idx)"
                        v-bind:id="'item-' + getFlatIndex(group.type, idx)"
                        class="result-row" link>
                        <v-list-item-title class="result-name">
                            {{ result.name }}
                        </v-list-item-title>

                        <!-- Campaign -->
                        <v-list-item-subtitle v-if="result.type === 'campaign'" class="result-meta">
                            <span class="text-capitalize">{{ getStatusIcon(result).label }}</span>
                            <span> | {{ formatDateShort(result.launch_date) }} &ndash; {{ formatDateShort(result.end_date) }}</span>
                        </v-list-item-subtitle>

                        <!-- Creative -->
                        <v-list-item-subtitle v-else-if="result.type === 'creative'" class="result-meta">
                            <span class="text-capitalize">{{ getStatusIcon(result).label }}</span>
                            <span> | {{ result.width }}&times;{{ result.height }}
                                <span v-if="result.language"> | {{ getLanguageName(result.language) }}</span>
                            </span>
                        </v-list-item-subtitle>

                        <!-- Profile -->
                        <v-list-item-subtitle v-else-if="result.type === 'profile'" class="result-meta">
                            <span class="text-capitalize">{{ getStatusIcon(result).label }}</span>
                            <span> | Updated: {{ formatDateShort(result.updated_at) }}</span>
                        </v-list-item-subtitle>
                    </v-list-item>
                </template>
            </v-list>
        </div>
    </v-card>
</template>

<script>
import debounce from "lodash/debounce";
import eventBus from "../eventBus";
import { formatDateShort } from "@/utils/formatters";
import { getStatusIcon } from "@/stores/helpers";
import { getLanguageName } from "@/constants/languages";
import { SECTION_COLORS } from "@/utils/sectionColors";

const GROUP_ORDER = ['campaign', 'creative', 'profile'];
const GROUP_LABELS = {
    campaign: 'Campaigns',
    creative: 'Creatives',
    profile: 'Profiles',
};

export default {
    props: {
        searchQuery: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            currentItem: 0,
            searchResults: [],
            loading: true,
        };
    },
    computed: {
        groupedResults() {
            if (!Array.isArray(this.searchResults) || !this.searchResults.length) return [];
            const groups = {};
            for (const result of this.searchResults) {
                if (!groups[result.type]) groups[result.type] = [];
                groups[result.type].push(result);
            }
            return GROUP_ORDER
                .filter(type => groups[type]?.length)
                .map(type => ({
                    type,
                    label: GROUP_LABELS[type],
                    color: SECTION_COLORS[type].icon,
                    results: groups[type],
                }));
        },
        flatResults() {
            const flat = [];
            for (const group of this.groupedResults) {
                for (const result of group.results) {
                    flat.push(result);
                }
            }
            return flat;
        },
    },
    methods: {
        formatDateShort,
        getStatusIcon,
        getLanguageName,

        goToView(viewName, id) {
            return {
                name: `${viewName}.view`,
                params: {
                    id: id,
                },
            };
        },
        getFlatIndex(type, idx) {
            let offset = 0;
            for (const group of this.groupedResults) {
                if (group.type === type) return offset + idx;
                offset += group.results.length;
            }
            return offset + idx;
        },
        readResults(payload) {
            this.loading = true;
            return new Promise((resolve, reject) => {
                axios
                    .get(`/v1/search/${payload}`)
                    .then(function (response) {
                        resolve(response);
                    })
                    .catch(function (error) {
                        reject(error);
                    });
            });
        },
        updateResults() {
            this.currentItem = 0;
            let queryUsed = btoa(this.searchQuery);

            this.readResults(queryUsed)
                .then((response) => {
                    let currentQuery = btoa(this.searchQuery);

                    if (currentQuery !== queryUsed) {
                        return;
                    }

                    this.searchResults = response.data?.length
                        ? response.data
                        : [];

                    this.loading = false;
                })
                .catch(() => {
                    this.searchResults = [];
                    this.loading = false;
                })
                .finally(() => { });
        },
        nextItem() {
            const resultsLength = this.flatResults.length - 1;

            const pressedDown = event.keyCode == 38 && this.currentItem > 0;
            const pressedUp =
                event.keyCode == 40 && this.currentItem < resultsLength;
            const pressedEnter = event.keyCode == 13;

            if (pressedDown) {
                this.currentItem--;
                document.getElementById(`item-${this.currentItem}`)?.scrollIntoView({ block: "nearest" });
            } else if (pressedUp) {
                this.currentItem++;
                document.getElementById(`item-${this.currentItem}`)?.scrollIntoView({ block: "nearest" });
            } else if (pressedEnter && this.flatResults[this.currentItem]) {
                let selectedObj = this.flatResults[this.currentItem];
                this.$router.push(this.goToView(selectedObj.type, selectedObj.id));
                eventBus.emit("CLEAR_SEARCH_RESULTS", null);
            }
        },
    },
    watch: {
        searchQuery: debounce(function () {
            this.updateResults();
        }, 400),
        $route() {
            eventBus.emit("CLEAR_SEARCH_RESULTS", null);
        },
    },
    created() {
        this.updateResults();
        window.addEventListener("keyup", this.nextItem);
    },
    unmounted() {
        window.removeEventListener("keyup", this.nextItem);
    },
};
</script>

<style scoped>
.scrollDiv {
    max-height: 75vh;
    overflow: auto;
}

.search-divider {
    border-width: 0.5px;
    opacity: 0.3;
}

.section-dot {
    width: 6px;
    height: 6px;
    border-radius: 1px;
    display: inline-block;
    margin-right: 8px;
    flex-shrink: 0;
}

.section-label {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: rgba(var(--v-theme-on-surface), 0.55);
}

.result-row {
    padding-left: 28px !important;
}

.result-name {
    font-size: 12px !important;
    font-weight: 500;
}

.result-meta {
    font-size: 10px !important;
    color: rgba(var(--v-theme-on-surface), 0.65) !important;
    /* Vuetify dims subtitles with its own opacity on top of the rgba alpha */
    opacity: 1;
}
</style>
